import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Table, Tooltip, Button, Card, Timeline, Progress, Spin, Select, Badge, Row, Col, Statistic, Tabs} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AppContext } from '../../context/ContextProvider';
import noDataImg from '@iso/assets/images/no-data-found.svg';
import rootCauseAnalysisIcon from '@iso/assets/images/root-cause-analysis-icon.svg';
import { use } from 'react';

const RootCauseAnalysisModal = ({ rca }) => {
  const analysis = rca.analysis;

  return (
    <div style={{ padding: '16px' }}>
      {/* Root Cause */}
      <Card
        title="🔍 Root Cause"
        size="small"
        style={{ marginBottom: '16px' }}
        headStyle={{ backgroundColor: '#f0f2f5', fontWeight: 600 }}
      >
        <p style={{ fontFamily: 'Nunito Sans', fontSize: '14px', margin: 0 }}>
          {analysis.root_cause}
        </p>
      </Card>

      {/* Contributing Factors */}
      <Card
        title="⚠️ Contributing Factors"
        size="small"
        style={{ marginBottom: '16px' }}
        headStyle={{ backgroundColor: '#fff7e6', fontWeight: 600 }}
      >
        <ul style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: 0, paddingLeft: '20px' }}>
          {analysis.contributing_factors.map((factor, index) => (
            <li key={index} style={{ marginBottom: '4px' }}>{factor}</li>
          ))}
        </ul>
      </Card>

      {/* Impact Assessment */}
      <Card
        title="📊 Impact Assessment"
        size="small"
        style={{ marginBottom: '16px' }}
        headStyle={{ backgroundColor: '#f6ffed', fontWeight: 600 }}
      >
        <p style={{ fontFamily: 'Nunito Sans', fontSize: '14px', margin: 0 }}>
          {analysis.impact_assessment}
        </p>
      </Card>

      {/* Recommended Actions */}
      <Card
        title="✅ Recommended Actions"
        size="small"
        style={{ marginBottom: '16px' }}
        headStyle={{ backgroundColor: '#e6f7ff', fontWeight: 600 }}
      >
        <ol style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: 0, paddingLeft: '20px' }}>
          {analysis.recommended_actions.map((action, index) => (
            <li key={index} style={{ marginBottom: '6px' }}>{action}</li>
          ))}
        </ol>
      </Card>

      {/* Timeline */}
      <Card
        title="⏰ Issue Timeline"
        size="small"
        style={{ marginBottom: '16px' }}
        headStyle={{ backgroundColor: '#fff1f0', fontWeight: 600 }}
      >
        <Timeline
          items={analysis.timeline.map((item, index) => {
            const getTimelineColor = (impact) => {
              switch (impact) {
                case 'high': return 'red';
                case 'medium': return 'orange';
                case 'low': return 'blue';
                case 'info': return 'blue';
                default: return 'blue';
              }
            };

            return {
              children: (
                <div>
                  <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px' }}>{item.time}</strong>
                  {item.source && (
                    <span style={{
                      fontFamily: 'Nunito Sans',
                      fontSize: '10px',
                      color: '#999',
                      marginLeft: '8px',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {item.source}
                    </span>
                  )}
                  <br />
                  <span style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>
                    {item.event}
                  </span>
                </div>
              ),
              color: item.impact ? getTimelineColor(item.impact) : (index === 0 ? 'red' : index === analysis.timeline.length - 1 ? 'orange' : 'blue')
            };
          })}
          size="small"
        />
      </Card>

      {/* Diagnostics */}
      {analysis.diagnostics && (
        <Card
          title="🔧 Diagnostic Information"
          size="small"
          style={{ marginBottom: '16px' }}
          headStyle={{ backgroundColor: '#f0f9ff', fontWeight: 600 }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {analysis.diagnostics.exit_codes && (
              <div>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Exit Codes:</strong>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: '4px 0' }}>
                  {analysis.diagnostics.exit_codes.join(', ')}
                </p>
              </div>
            )}
            {analysis.diagnostics.restart_counts && (
              <div>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Restart Counts:</strong>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: '4px 0' }}>
                  {analysis.diagnostics.restart_counts.join(', ')}
                </p>
              </div>
            )}
            {analysis.diagnostics.resource_usage && (
              <div>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Resource Usage:</strong>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: '4px 0' }}>
                  {analysis.diagnostics.resource_usage}
                </p>
              </div>
            )}
            {analysis.diagnostics.network_connectivity && (
              <div>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Network:</strong>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: '4px 0' }}>
                  {analysis.diagnostics.network_connectivity}
                </p>
              </div>
            )}
            {analysis.diagnostics.service_info && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Service Info:</strong>
                <p style={{ fontFamily: 'Nunito Sans', fontSize: '13px', margin: '4px 0' }}>
                  Type: {analysis.diagnostics.service_info.type},
                  Endpoints: {analysis.diagnostics.service_info.endpoints},
                  Selector: {analysis.diagnostics.service_info.selector}
                </p>
              </div>
            )}
            {analysis.diagnostics.container_logs && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong style={{ fontFamily: 'Nunito Sans', fontSize: '12px', color: '#666' }}>Container Logs:</strong>
                <pre style={{
                  fontFamily: 'Monaco, monospace',
                  fontSize: '11px',
                  backgroundColor: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  margin: '4px 0',
                  maxHeight: '100px',
                  overflow: 'auto'
                }}>
                  {analysis.diagnostics.container_logs}
                </pre>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confidence & Resolution Time */}
      <div style={{ display: 'flex', gap: '16px' }}>
        <Card
          title="🎯 Confidence Score"
          size="small"
          style={{ flex: 1 }}
          headStyle={{ backgroundColor: '#f9f0ff', fontWeight: 600 }}
        >
          <Progress
            percent={Math.round(analysis.confidence_score * 100)}
            status={analysis.confidence_score > 0.8 ? 'success' : analysis.confidence_score > 0.6 ? 'normal' : 'exception'}
            strokeColor={analysis.confidence_score > 0.8 ? '#52c41a' : analysis.confidence_score > 0.6 ? '#1890ff' : '#ff4d4f'}
          />
        </Card>

        <Card
          title="⏱️ Est. Resolution Time"
          size="small"
          style={{ flex: 1 }}
          headStyle={{ backgroundColor: '#f0f9ff', fontWeight: 600 }}
        >
          <p style={{
            fontFamily: 'Nunito Sans',
            fontSize: '16px',
            fontWeight: 600,
            margin: 0,
            color: '#1890ff',
            textAlign: 'center'
          }}>
            {analysis.estimated_resolution_time}
          </p>
        </Card>
      </div>
    </div>
  );
};



const HealthIssuesRightDrawer = ({
  data,
  closeDrawer,
  openDrawer
}) => {
  const [healthIssues, setHealthIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rcaLoading, setRcaLoading] = useState({});
  const [, setRcaResults] = useState({});
  
  // New state for tab management and separate data
  const [activeTab, setActiveTab] = useState('active');
  const [activeIssues, setActiveIssues] = useState([]);
  const [resolvedIssues, setResolvedIssues] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  
  const { checkTryWithoutLogin } = useContext(AppContext);
  
  // Filter states
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  let rcaController = useRef(null); // Placeholder for any future useRef needs

  console.log("data in HealthIssuesRightDrawer :", data);
  const clusterId = data?.clusterId;
  const envNamespace = data?.envNamespace || '';
  const forceActualAPI = data?.forceActualAPI || false;

  // Check if we're in try without login mode (but override if forceActualAPI is true)
  const isTryWithoutLogin = !forceActualAPI && checkTryWithoutLogin;    // process.env.REACT_APP_ENVIRONMENT === 'local'

  // Create a more dynamic cluster name
  const getClusterDisplayName = () => {
    if (data?.clusterName && !data.clusterName.includes('Test Cluster')) {
      return data.clusterName;
    }

    if (forceActualAPI) {
      return `Cluster ${clusterId} (Live)`;
    }

    // Try to get a more descriptive name based on environment

    if (isTryWithoutLogin && clusterId) {
      return `Demo Cluster ${clusterId}`;
    }

    if (!clusterId) {
      return `No Data`;
    }

    return `Cluster ${clusterId}`;
  };

  const clusterName = getClusterDisplayName();

  // Load user preferences from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(`healthIssuesFilters_${clusterId}`);
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSelectedSeverity(filters.severity || 'all');
        setSelectedCategory(filters.category || 'all');
      } catch (e) {
        console.log('Error loading saved filters:', e);
      }
    }
  }, [clusterId]);

  // Initialize filteredIssues when healthIssues are loaded
  useEffect(() => {
    setFilteredIssues(healthIssues);
  }, [healthIssues]);

  // Reset filters when switching tabs
  useEffect(() => {
    setSelectedSeverity('all');
    setSelectedCategory('all');
  }, [activeTab]);

  // Save user preferences to localStorage
  const saveFilters = useCallback((filters) => {
    try {
      localStorage.setItem(`healthIssuesFilters_${clusterId}`, JSON.stringify(filters));
    } catch (e) {
      console.log('Error saving filters:', e);
    }
  }, [clusterId]);

  // Apply filters and update filtered issues
  useEffect(() => {
    const currentIssues = activeTab === 'active' ? activeIssues : resolvedIssues;
    let filtered = [...currentIssues];

    if (selectedSeverity !== 'all') {
      filtered = filtered.filter(issue => issue.severity === selectedSeverity);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(issue => issue.category === selectedCategory);
    }

    setFilteredIssues(filtered);

    // Save current filters
    saveFilters({
      severity: selectedSeverity,
      category: selectedCategory
    });
  }, [activeTab, activeIssues, resolvedIssues, selectedSeverity, selectedCategory]);

  // Get unique values for filter options based on current tab
  const getUniqueValues = (field) => {
    const currentIssues = activeTab === 'active' ? activeIssues : resolvedIssues;
    const values = [...new Set(currentIssues.map(issue => issue[field]))];
    return values.sort();
  };

  // Get issue counts by severity for current tab
  const getIssueCounts = () => {
    const currentIssues = activeTab === 'active' ? activeIssues : resolvedIssues;
    const counts = {
      critical: 0,
      high: 0,
      warning: 0,
      info: 0,
      total: currentIssues.length
    };

    currentIssues.forEach(issue => {
      const severity = issue.severity?.toLowerCase();
      if (severity === 'critical') counts.critical++;
      else if (severity === 'high') counts.high++;
      else if (severity === 'warning') counts.warning++;
      else if (severity === 'info') counts.info++;
    });

    return counts;
  };

  const issueCounts = getIssueCounts();

  // Issue summary statistics
  const renderIssueSummary = () => (
    <div style={{ 
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #f0f0f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Statistic
            title="Active Issues"
            value={activeIssues.length}
            valueStyle={{ color: '#d20a0a', fontSize: '24px', fontWeight: '600' }}
            prefix={<Badge color="#d20a0a" />}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Resolved Issues"
            value={resolvedIssues.length}
            valueStyle={{ color: '#53a551', fontSize: '24px', fontWeight: '600' }}
            prefix={<Badge color="#53a551" />}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Critical (Active)"
            value={activeIssues.filter(issue => issue.severity === 'critical' || issue.severity === 'high').length}
            valueStyle={{ color: '#d20a0a', fontSize: '20px', fontWeight: '600' }}
            prefix={<Badge color="#d20a0a" />}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Warning (Active)"
            value={activeIssues.filter(issue => issue.severity === 'warning' || issue.severity === 'medium').length}
            valueStyle={{ color: '#fa8c16', fontSize: '20px', fontWeight: '600' }}
            prefix={<Badge color="#fa8c16" />}
          />
        </Col>
      </Row>
    </div>
  );

  const fetchHealthIssues = useCallback(async () => {
    setLoading(true);
    setActiveLoading(true);
    setResolvedLoading(true);
    setError(null);
    // Use sample data for try without login
    const sampleData = [
      {
        id: 1,
        title: 'High CPU Usage',
        description: 'CPU usage is consistently above 80% for the last 24 hours',
        severity: 'high',
        category: 'performance',
        resource_type: 'Pod',
        resource_name: 'web-server-deployment-7d8f9c6b4-xyz12',
        namespace: 'default',
        created_at: '2024-01-15T10:30:00Z',
        status: 'active',
        affected_resources: 3,
        recommendation: 'Consider increasing resource limits or scaling horizontally'
      },
      {
        id: 2,
        title: 'Memory Leak Detected',
        description: 'Memory usage increasing steadily without being released',
        severity: 'medium',
        category: 'memory',
        resource_type: 'Container',
        resource_name: 'api-service-container',
        namespace: 'production',
        created_at: '2024-01-15T09:15:00Z',
        status: 'investigating',
        affected_resources: 1,
        recommendation: 'Review application code for memory leaks and restart container'
      },
      {
        id: 3,
        title: 'Network Connectivity Issue',
        description: 'Intermittent connection failures to external services',
        severity: 'high',
        category: 'network',
        resource_type: 'Service',
        resource_name: 'external-api-service',
        namespace: 'default',
        created_at: '2024-01-15T08:45:00Z',
        status: 'active',
        affected_resources: 5,
        recommendation: 'Check network policies and firewall rules'
      },
      {
        id: 4,
        title: 'Storage Space Low',
        description: 'Persistent volume usage above 85%',
        severity: 'medium',
        category: 'storage',
        resource_type: 'PersistentVolume',
        resource_name: 'data-storage-pv',
        namespace: 'database',
        created_at: '2024-01-15T07:20:00Z',
        status: 'resolved',
        affected_resources: 2,
        recommendation: 'Expand storage capacity or clean up old data'
      },
      {
        id: 5,
        title: 'Pod Restart Loop',
        description: 'Pod continuously restarting due to failed health checks',
        severity: 'high',
        category: 'reliability',
        resource_type: 'Pod',
        resource_name: 'worker-pod-abc123',
        namespace: 'workers',
        created_at: '2024-01-15T06:10:00Z',
        status: 'active',
        affected_resources: 1,
        recommendation: 'Review health check configuration and application logs'
      },
      {
        id: 6,
        title: 'Security Vulnerability',
        description: 'Container image has known security vulnerabilities',
        severity: 'high',
        category: 'security',
        resource_type: 'Container',
        resource_name: 'nginx-container',
        namespace: 'web',
        created_at: '2024-01-15T05:30:00Z',
        status: 'active',
        affected_resources: 4,
        recommendation: 'Update container image to latest version'
      },
      {
        id: 7,
        title: 'Database Connection Pool Full',
        description: 'Database connection pool reached maximum capacity',
        severity: 'medium',
        category: 'database',
        resource_type: 'Service',
        resource_name: 'postgres-service',
        namespace: 'database',
        created_at: '2024-01-15T04:15:00Z',
        status: 'investigating',
        affected_resources: 2,
        recommendation: 'Increase connection pool size or optimize queries'
      },
      {
        id: 8,
        title: 'Disk I/O Bottleneck',
        description: 'High disk I/O wait times affecting performance',
        severity: 'medium',
        category: 'performance',
        resource_type: 'Node',
        resource_name: 'worker-node-2',
        namespace: 'kube-system',
        created_at: '2024-01-15T03:45:00Z',
        status: 'resolved',
        affected_resources: 8,
        recommendation: 'Consider using faster storage or optimizing I/O operations'
      },
      {
        id: 9,
        title: 'Load Balancer Unhealthy',
        description: 'Load balancer health check failing for backend services',
        severity: 'high',
        category: 'network',
        resource_type: 'Service',
        resource_name: 'frontend-lb-service',
        namespace: 'default',
        created_at: '2024-01-15T02:20:00Z',
        status: 'active',
        affected_resources: 3,
        recommendation: 'Check backend service health and load balancer configuration'
      },
      {
        id: 10,
        title: 'Certificate Expiring Soon',
        description: 'SSL certificate will expire within 30 days',
        severity: 'low',
        category: 'security',
        resource_type: 'Secret',
        resource_name: 'tls-certificate',
        namespace: 'default',
        created_at: '2024-01-15T01:10:00Z',
        status: 'acknowledged',
        affected_resources: 1,
        recommendation: 'Renew SSL certificate before expiration'
      },
      {
        id: 11,
        title: 'Image Pull Failure',
        description: 'Unable to pull container image from registry',
        severity: 'medium',
        category: 'deployment',
        resource_type: 'Pod',
        resource_name: 'new-feature-pod',
        namespace: 'staging',
        created_at: '2024-01-15T00:30:00Z',
        status: 'active',
        affected_resources: 1,
        recommendation: 'Check image registry credentials and image availability'
      },
      {
        id: 12,
        title: 'Resource Quota Exceeded',
        description: 'Namespace has exceeded its resource quota limits',
        severity: 'medium',
        category: 'resource',
        resource_type: 'Namespace',
        resource_name: 'development',
        namespace: 'development',
        created_at: '2024-01-14T23:45:00Z',
        status: 'investigating',
        affected_resources: 6,
        recommendation: 'Review and adjust resource quota or optimize resource usage'
      }
    ];

    try {
      if (isTryWithoutLogin) {

        // Separate active and resolved issues
        const active = sampleData.filter(issue => issue.status !== 'resolved');
        const resolved = sampleData.filter(issue => issue.status === 'resolved');
        
        setActiveIssues(active);
        setResolvedIssues(resolved);
        setHealthIssues(sampleData); // Keep for backward compatibility
      } else {
        // Real API call to local kubegateway using fetch
        try {
          // Fetch active issues
          console.log(`[HEALTH_ISSUES] Fetching active issues from: ${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/?cluster_id=${clusterId}&page=1&per_page=100&status=active&namespace=${envNamespace}`);
          const accessToken = localStorage.getItem('isAuth');
          const activeResponse = await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/?cluster_id=${clusterId}&page=1&per_page=100&status=active&namespace=${envNamespace}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          });

          // Fetch resolved issues
          console.log(`[HEALTH_ISSUES] Fetching resolved issues from: ${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/?cluster_id=${clusterId}&page=1&per_page=100&status=resolved&namespace=${envNamespace}`);
          const resolvedResponse = await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/?cluster_id=${clusterId}&page=1&per_page=100&status=resolved&namespace=${envNamespace}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          });

          console.log('[HEALTH_ISSUES] Active issues response status:', activeResponse.status);
          console.log('[HEALTH_ISSUES] Resolved issues response status:', resolvedResponse.status);

          if (activeResponse.ok && resolvedResponse.ok) {
            const activeData = await activeResponse.json();
            const resolvedData = await resolvedResponse.json();

            console.log('[HEALTH_ISSUES] Active issues data:', activeData);
            console.log('[HEALTH_ISSUES] Resolved issues data:', resolvedData);

            // Transform active issues
            const transformedActiveIssues = (activeData.issues || []).map(item => ({
              id: item.id,
              issue_id: item.issue.id || '',
              title: item.issue.title || 'Unknown Issue',
              description: item.issue.description || 'No description available',
              severity: item.issue.severity || 'unknown',
              issue_type: item.issue.type || 'unknown',
              category: item.issue.scope || 'general',
              resource_type: item.issue.scope || 'Unknown',
              resource_name: item.issue.resource || 'Unknown',
              namespace: item.issue.namespace || 'default',
              created_at: item.issue.detected_at || item.created_at,
              status: item.issue.status || 'active',
              pod_name: item.issue?.details?.pod_name || 'unknown',
              affected_resources: item.issue.state?.report_count || 1,
              recommendation: `Review and fix ${item.issue.title || 'this issue'}`
            }));

            // Transform resolved issues
            const transformedResolvedIssues = (resolvedData.issues || []).map(item => ({
              id: item.id,
              issue_id: item.issue.id || '',
              title: item.issue.title || 'Unknown Issue',
              description: item.issue.description || 'No description available',
              severity: item.issue.severity || 'unknown',
              issue_type: item.issue.type || 'unknown',
              category: item.issue.scope || 'general',
              resource_type: item.issue.scope || 'Unknown',
              resource_name: item.issue.resource || 'Unknown',
              namespace: item.issue.namespace || 'default',
              created_at: item.issue.detected_at || item.created_at,
              status: item.issue.status || 'resolved',
              pod_name: item.issue?.details?.pod_name || 'unknown',
              affected_resources: item.issue.state?.report_count || 1,
              recommendation: `Issue has been resolved`
            }));

            console.log(`[HEALTH_ISSUES] Transformed ${transformedActiveIssues.length} active issues and ${transformedResolvedIssues.length} resolved issues`);
            
            setActiveIssues(transformedActiveIssues);
            setResolvedIssues(transformedResolvedIssues);
            setHealthIssues([...transformedActiveIssues, ...transformedResolvedIssues]); // Keep for backward compatibility
          } else {
            console.log(`[HEALTH_ISSUES] API calls failed - Active: ${activeResponse.status}, Resolved: ${resolvedResponse.status}`);
            setActiveIssues([]);
            setResolvedIssues([]);
            setHealthIssues([]);
            setError(`API calls failed - Active: ${activeResponse.status}, Resolved: ${resolvedResponse.status}`);
          }
        } catch (apiError) {
          console.error('[HEALTH_ISSUES] API calls failed:', apiError);
          setActiveIssues([]);
          setResolvedIssues([]);
          // setHealthIssues([]);
          // let items = sampleData.map(item => ({
          //   id: item.id,
          //   issue_id: item.issue?.id || '',
          //   title: item.issue?.title || 'Unknown Issue',
          //   description: item.issue?.description || 'No description available',
          //   severity: item.issue?.severity || 'unknown',
          //   issue_type: item.issue?.type || 'unknown',
          //   category: item.issue?.scope || 'general',
          //   resource_type: item.issue?.scope || 'Unknown',
          //   resource_name: item.issue?.resource || 'Unknown',
          //   namespace: item.issue?.namespace || 'default',
          //   created_at: item.issue?.detected_at || item.created_at,
          //   status: item.issue?.status || 'active',
          //   pod_name: item.issue?.details?.pod_name || 'unknown',
          //   affected_resources: item.issue?.state?.report_count || 1, // Use report_count from API or default to 1
          //   recommendation: `Review and fix ${item.issue?.title || 'this issue'}`
          // }));
          // console.log("items in HealthIssuesRightDrawer :", items);
          setHealthIssues([]);
          setError(`API calls failed: ${apiError.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error fetching health issues:', error);
      setError('Failed to fetch health issues');
    } finally {
      setLoading(false);
      setActiveLoading(false);
      setResolvedLoading(false);
    }
  }, [forceActualAPI, checkTryWithoutLogin, clusterId]);

  useEffect(() => {
    if (clusterId) {
      fetchHealthIssues();
    }
  }, [clusterId, fetchHealthIssues]);

  useEffect(() => {
    return () => {
      rcaController.current?.abort(); // Clean up any ongoing requests
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const markAsResolved = async (issueId, issue) => {
    try {
      // Check if we're in try without login mode (but override if forceActualAPI is true)
      if (isTryWithoutLogin) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Update local state to mark as resolved
        setHealthIssues(prev =>
          prev.map(item =>
            item.id === issueId
              ? { ...item, status: 'resolved' }
              : item
          )
        );

        // Show success message
        Modal.success({
          title: 'Issue Resolved',
          content: `${issue.title} has been marked as resolved successfully.`,
          okText: 'Close'
        });
      } else {
        // Make actual API call
        const accessToken = localStorage.getItem('isAuth');
        await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/${issueId}/`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          signal: AbortSignal.timeout(10000) // 10s timeout
        });

        // Update local state
        setHealthIssues(prev =>
          prev.map(item =>
            item.id === issueId
              ? { ...item, status: 'resolved' }
              : item
          )
        );

        // Show success message
        Modal.success({
          title: 'Issue Resolved',
          content: `${issue.title} has been marked as resolved successfully.`,
          okText: 'Close'
        });
      }
    } catch (error) {
      console.error('Error marking issue as resolved:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to mark issue as resolved. Please try again.',
        okText: 'Close'
      });
    }
  };

  const runRootCauseAnalysis = async (issueId, issue) => {
    setRcaLoading(prev => ({ ...prev, [issueId]: true }));
    console.log("issue in runRootCauseAnalysis :", issue);

    try {
      // Check if we're in try without login mode (but override if forceActualAPI is true)
      if (isTryWithoutLogin) {   // isTryWithoutLogin
      // if (true) {   // isTryWithoutLogin
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Show sample RCA data since the API is not fully implemented yet
        const sampleRcaData = {
          analysis: {
            root_cause: "Container exit with code 1",
            contributing_factors: [
              "Container command failed with non-zero exit code",
              "Application error or misconfiguration",
              "Resource constraints or environment issues"
            ],
            impact_assessment: `Issue affects ${issue.resource} in ${issue.namespace} namespace. Status: ${issue.status}. Timeline spans 30m52s with 4 total events.`,
            recommended_actions: [
              "Review container command and exit codes",
              "Implement proper error handling in application",
              "Add proper health checks",
              "Monitor resource usage and limits",
              "Check application logs for specific error details"
            ],
            timeline: [
              {
                time: "22:44:08",
                event: "PodCreated: Pod test-pod-error was created",
                impact: "info",
                source: "pod"
              },
              {
                time: "22:44:15",
                event: "Pulling: Pulling image \"busybox\"",
                impact: "info",
                source: "kubernetes"
              },
              {
                time: "22:44:18",
                event: "Started: Started container error-container",
                impact: "info",
                source: "kubernetes"
              },
              {
                time: "22:44:20",
                event: "Failed: Container exited with code 1",
                impact: "high",
                source: "kubernetes"
              }
            ],
            confidence_score: 0.75, // Medium confidence for sample data
            estimated_resolution_time: "15-30 minutes based on recommendations",
            diagnostics: {
              exit_codes: [1],
              restart_counts: [0],
              resource_usage: "Normal",
              network_connectivity: "Not applicable",
              container_logs: "Error: Command failed with exit code 1\n/bin/sh: 1: exit: Illegal number: 1\nContainer terminated"
            }
          }
        };

        setRcaResults(prev => ({ ...prev, [issueId]: sampleRcaData }));

        // Show RCA results with sample data
        Modal.info({
          title: `Root Cause Analysis - ${issue.title}`,
          width: '80vw',
          content: (
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{
                backgroundColor: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: '4px',
                padding: '8px 12px',
                marginBottom: '16px',
                fontSize: '12px',
                color: '#d48806'
              }}>
                <strong>Note:</strong> This is sample analysis data. The RCA service is currently being developed.
              </div>
              <RootCauseAnalysisModal rca={sampleRcaData} />
            </div>
          ),
          footer: (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                // type="primary"
                style={{
                  backgroundColor: '#132f44',
                  // borderColor: '#52c41a',
                  color: "#FFFFFF",
                  fontFamily: 'Nunito Sans',
                  fontWeight: 600
                }}
                onClick={() => {
                  Modal.destroyAll();
                  markAsResolved(issueId, issue);
                }}
              >
                Mark as Resolved
              </Button>
              <Button
                onClick={() => Modal.destroyAll()}
                style={{
                  fontFamily: 'Nunito Sans',
                  fontWeight: 600
                }}
              >
                Close
              </Button>
            </div>
          ),
          icon: null
        });

        // Show that RCA is not available in try-without-login mode
        // Modal.info({
        //   title: `Root Cause Analysis - ${issue.title}`,
        //   width: '60vw',
        //   content: (
        //     <div style={{ textAlign: 'center', padding: '40px' }}>
        //       <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        //       <h3 style={{ 
        //         fontFamily: 'Nunito Sans', 
        //         fontSize: '18px',
        //         fontWeight: 600,
        //         color: '#666',
        //         marginBottom: '8px'
        //       }}>
        //         Root Cause Analysis Not Available
        //       </h3>
        //       <p style={{ 
        //         fontFamily: 'Nunito Sans', 
        //         fontSize: '14px',
        //         color: '#999',
        //         margin: 0
        //       }}>
        //         Root cause analysis is not available in try-without-login mode. 
        //         Please log in to access the full analysis features.
        //       </p>
        //     </div>
        //   ),
        //   footer: (
        //     <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        //       <Button
        //         type="primary"
        //         style={{
        //           backgroundColor: '#52c41a',
        //           borderColor: '#52c41a',
        //           fontFamily: 'Nunito Sans',
        //           fontWeight: 600
        //         }}
        //         onClick={() => {
        //           Modal.destroyAll();
        //           markAsResolved(issueId, issue);
        //         }}
        //       >
        //         Mark as Resolved
        //       </Button>
        //       <Button
        //         onClick={() => Modal.destroyAll()}
        //         style={{
        //           fontFamily: 'Nunito Sans',
        //           fontWeight: 600
        //         }}
        //       >
        //         Close
        //       </Button>
        //     </div>
        //   ),
        //   icon: null
        // });
      } else {
        // Real API call to local kubegateway
        try {
          rcaController.current?.abort(); // Abort any previous request if it exists

          rcaController.current = new AbortController(); // Create a new controller for this request

          const accessToken = localStorage.getItem('isAuth');
          const response = await fetch(`${process.env.REACT_APP_KUBEGATEWAY}/api/health-issues/${issue.id}/ai-enhanced-root-cause-analysis`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              // cluster_id: clusterId,       // 922,
              // issue_details: issue
              "issue_id": issue.id,
              "issue_type": issue.issue_type,
              "namespace": issue.namespace,
              "resource": issue.resource_name,
              // "details": {
              //   "pod_name": issue.pod_name,
              //   "namespace": issue.namespace,
              //   "restart_count": issue.affected_resources
              // }
            }),
            signal: rcaController.current.signal                // AbortSignal.timeout(10000) // 10s timeout
          });
          
          console.log("response in RCA :", response);
          console.log("response.ok :", response.ok);
          if (response.ok) {
            const data = await response.json();
            
            console.log("data in RCA :", data);

            if (data && data.analysis?.findings) {
              // Transform the API response to match the expected format for RootCauseAnalysisModal
              const transformedData = {
                analysis: {
                  root_cause: data.analysis.findings.primary_cause || 'Unknown root cause',
                  contributing_factors: data.analysis.findings.contributing_factors || [],
                  impact_assessment: `Issue affects ${data.analysis.resource} in ${data.analysis.namespace} namespace. Status: ${data.analysis.status}. ${data.analysis.timeline?.summary || ''}`,
                  recommended_actions: data.analysis.findings.recommendations || [],
                  timeline: data.analysis.timeline?.events ? data.analysis.timeline.events.map((event, index) => ({
                    time: new Date(event.timestamp).toLocaleTimeString(),
                    event: `${event.event}: ${event.description}`,
                    impact: event.impact,
                    source: event.source
                  })) : data.analysis.diagnostics?.pod_events ? data.analysis.diagnostics.pod_events.map((event, index) => ({
                    time: `Event ${index + 1}`,
                    event: event
                  })) : [{ time: data.timestamp, event: 'Root cause analysis completed' }],
                  confidence_score: data.analysis.confidence_score ?? data.analysis.remediation_proposals?.[0]?.confidence_score ?? 0.95,
                  estimated_resolution_time: data.analysis.timeline?.duration ? `Analysis took ${data.analysis.timeline.duration}` : 'Based on recommendations provided',
                  diagnostics: {
                    container_logs: data.analysis.diagnostics?.container_logs,
                    exit_codes: data.analysis.diagnostics?.exit_codes,
                    restart_counts: data.analysis.diagnostics?.restart_counts,
                    resource_usage: data.analysis.diagnostics?.resource_usage,
                    network_connectivity: data.analysis.diagnostics?.network_connectivity,
                    service_info: data.analysis.diagnostics?.service_type ? {
                      type: data.analysis.diagnostics.service_type,
                      endpoints: data.analysis.diagnostics.endpoints_count,
                      selector: data.analysis.diagnostics.selector
                    } : null
                  }
                }
              };

              setRcaResults(prev => ({ ...prev, [issueId]: transformedData }));

              // Show RCA results
              Modal.info({
                title: `Root Cause Analysis - ${issue.title}`,
                width: '80vw',
                content: (
                  <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: "20px" }}>
                    <RootCauseAnalysisModal rca={transformedData} />
                  </div>
                ),
                footer: (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: '#132f44',
                        // borderColor: '#52c41a',
                        color: "#FFFFFF",
                        fontFamily: 'Nunito Sans',
                        fontWeight: 600
                      }}
                      onClick={() => {
                        Modal.destroyAll();
                        markAsResolved(issueId, issue);
                      }}
                    >
                      Mark as Resolved
                    </Button>
                    <Button
                      onClick={() => Modal.destroyAll()}
                      style={{
                        fontFamily: 'Nunito Sans',
                        fontWeight: 600
                      }}
                    >
                      Close
                    </Button>
                  </div>
                ),
                icon: null
              });
            } else {
              // No RCA data available
              Modal.info({
                title: `Root Cause Analysis - ${issue.title}`,
                width: '60vw',
                content: (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                    <h3 style={{
                      fontFamily: 'Nunito Sans',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#666',
                      marginBottom: '8px'
                    }}>
                      Root Cause Analysis Not Available
                    </h3>
                    <p style={{
                      fontFamily: 'Nunito Sans',
                      fontSize: '14px',
                      color: '#999',
                      margin: 0
                    }}>
                      The root cause analysis for this issue is not available at the moment.
                    </p>
                  </div>
                ),
                footer: (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: '#132f44',
                        // borderColor: '#52c41a',
                        color: "#FFFFFF",
                        fontFamily: 'Nunito Sans',
                        fontWeight: 600
                      }}
                      onClick={() => {
                        Modal.destroyAll();
                        markAsResolved(issueId, issue);
                      }}
                    >
                      Mark as Resolved
                    </Button>
                    <Button
                      onClick={() => Modal.destroyAll()}
                      style={{
                        fontFamily: 'Nunito Sans',
                        fontWeight: 600
                      }}
                    >
                      Close
                    </Button>
                  </div>
                ),
                icon: null
              });
            }
          } else {
            // API call failed
            Modal.error({
              title: `Root Cause Analysis Failed`,
              width: '60vw',
              content: (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                  <h3 style={{
                    fontFamily: 'Nunito Sans',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#ff4d4f',
                    marginBottom: '8px'
                  }}>
                    Failed to Generate Root Cause Analysis
                  </h3>
                  <p style={{
                    fontFamily: 'Nunito Sans',
                    fontSize: '14px',
                    color: '#999',
                    margin: 0
                  }}>
                    API call failed with status: {response.status}
                  </p>
                </div>
              ),
              footer: (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: '#132f44',
                      // borderColor: '#52c41a',
                      color: "#FFFFFF",
                      fontFamily: 'Nunito Sans',
                      fontWeight: 600
                    }}
                    onClick={() => {
                      Modal.destroyAll();
                      markAsResolved(issueId, issue);
                    }}
                  >
                    Mark as Resolved
                  </Button>
                  <Button
                    onClick={() => Modal.destroyAll()}
                    style={{
                      fontFamily: 'Nunito Sans',
                      fontWeight: 600
                    }}
                  >
                    Close
                  </Button>
                </div>
              ),
              icon: null
            });
          }
        } catch (apiError) {
          console.error('RCA API call failed:', apiError);
        }
      }
    } catch (error) {
      console.error('Error running root cause analysis:', error);
    } finally {
      setRcaLoading(prev => ({ ...prev, [issueId]: false }));
    }
  };



  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#53A551';
      case 'investigating': return '#F6C344';
      case 'resolved': return '#4D99EF';
      case 'acknowledged': return '#4BA0B5';
      case 'pending': return '#722ed1';
      default: return '#8c8c8c';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return { color: '#dc3545', backgroundColor: '#fff', border: '#dc3545' };
      case 'high': return { color: '#D20A0A', backgroundColor: '#fff', border: '#D20A0A' };
      case 'medium': return { color: '#FF9C0F', backgroundColor: '#fff', border: '#FF9C0F' };
      case 'warning': return { color: '#fa8c16', backgroundColor: '#fff', border: '#fa8c16' };
      case 'low': return { color: '#00BA00', backgroundColor: '#fff', border: '#00BA00' };
      case 'info': return { color: '#1890ff', backgroundColor: '#fff', border: '#1890ff' };
      default: return { color: '#9a9a9a', backgroundColor: '#fff', border: '#9a9a9a' };
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'performance': return '#3579F6';
      case 'memory': return '#7E57C2';
      case 'network': return '#FB8C00';
      case 'storage': return '#43A047';
      case 'security': return '#CB444A';
      case 'database': return '#1E88E5';
      case 'reliability': return '#EC407A';
      case 'deployment': return '#00897B';
      case 'resource': return '#faad14';
      case 'configmap': return '#2f54eb';
      case 'pod': return '#40a9ff';
      case 'service': return '#13c2c2';
      case 'secret': return '#6C757D';
      default: return '#8c8c8c';
    }
  };

  const baseColumns = [
    {
      title: 'Issue Title',
      dataIndex: 'title',
      key: 'title',
      width: '200px',
      render: (text, record) => (
        <div>
          <Tooltip title={text}>
            <span style={{
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1f1f1f',
              display: 'block',
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{text}</span>
          </Tooltip>
          <Tooltip title={record.description}>
            <span style={{
              fontFamily: 'Nunito Sans',
              fontSize: '12px',
              fontWeight: 400,
              color: '#666',
              display: 'block',
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: '2px'
            }}>{record.description}</span>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '100px',
      render: (category) => (
        <span style={{
          fontFamily: 'Nunito Sans',
          fontSize: '14px',
          fontWeight: 500,
          color: getCategoryColor(category),
          border: `1px solid ${getCategoryColor(category)}`,
          padding: '4px 10px',
          borderRadius: '5px',
          textTransform: 'capitalize'
        }}>{category}</span>
      )
    },
    {
      title: 'Resource',
      dataIndex: 'resource_name',
      key: 'resource_name',
      width: '150px',
      render: (text, record) => (
        <div>
          <span style={{
            fontFamily: 'Nunito Sans',
            fontSize: '14px',
            fontWeight: 500,
            color: '#666',
            display: 'block'
          }}>{record.resource_type}</span>
          <Tooltip title={text}>
            <span style={{
              fontFamily: 'Nunito Sans',
              fontSize: '12px',
              fontWeight: 500,
              color: '#1f1f1f',
              display: 'block',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{text}</span>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Namespace',
      dataIndex: 'namespace',
      key: 'namespace',
      width: '100px',
      render: (text) => (
        <span style={{
          fontFamily: 'Nunito Sans',
          fontSize: '14px',
          fontWeight: 500,
          color: '#1f1f1f',
          textTransform: 'capitalize',
        }}>{text}</span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      render: (status) => (
        <span style={{
          fontFamily: 'Nunito Sans',
          fontSize: '14px',
          fontWeight: 500,
          color: '#fff',
          border: `1px solid ${getStatusColor(status)}`,
          backgroundColor: getStatusColor(status),
          padding: '4px 10px',
          borderRadius: '5px',
          textTransform: 'capitalize'
        }}>{status}</span>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: '80px',
      render: (severity) => {
        const severityColors = getSeverityColor(severity);
        return (
          <span style={{
            fontFamily: 'Nunito Sans',
            fontSize: '14px',
            fontWeight: 500,
            color: severityColors.color,
            // backgroundColor: severityColors.backgroundColor,
            padding: '4px 10px',
            borderRadius: '5px',
            border: `1px solid ${severityColors.border}`,
            textTransform: 'capitalize'
          }}>{severity}</span>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '120px',
      render: (dateString) => (
        <span style={{
          fontFamily: 'Nunito Sans',
          fontSize: '14px',
          fontWeight: 400,
          color: '#666'
        }}>{formatDate(dateString)}</span>
      )
    }
  ];

  const actionsColumn = {
    title: 'Actions',
    key: 'actions',
    width: '150px',
    render: (_, record) => (
      <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
        <Button
          size="small"
          type="primary"
          loading={rcaLoading[record.id]}
          onClick={() => runRootCauseAnalysis(record.id, record)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10",
            fontFamily: 'Nunito Sans',
            fontSize: '14px',
            fontWeight: 600,
            height: '29px',
            color: '#FFFFFF',
            backgroundColor: '#6486EE',
            borderColor: '#6486EE',
            borderRadius: '5px',
            padding: '4px 10px',
          }}
        >
          <img src={rootCauseAnalysisIcon} alt="Root Cause Analysis" style={{ width: "16px", height: "16px" }} />
          {rcaLoading[record.id] ? 'Wait analyzing.....' : 'Root Cause Analysis'}
        </Button>
      </div>
    )
  };

  // Dynamically build columns based on activeTab
  const columns = activeTab === 'active' 
    ? [...baseColumns.slice(0, 4), actionsColumn, ...baseColumns.slice(4)]
    : baseColumns;

  return (
    <div style={{ padding: '0px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px',
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: '15px',
        backgroundColor: "#132F44",
        padding: '20px',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div>
          <h2 style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '18px',
            color: '#FFFFFF',
            margin: 0,
            marginBottom: "5px",
          }}>
            Health Issues - {clusterName}
          </h2>
          <span style={{
            fontFamily: 'Nunito Sans',
            fontWeight: '600',
            fontSize: '14px',
            color: '#FFFFFF',
            margin: 0,
            // marginTop: "10px",
          }}>Comprehensive health monitoring and issue detection for your Kubernetes cluster resources.</span>
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={closeDrawer}
          style={{
            border: 'none',
            boxShadow: 'none',
            color: '#FFFFFF'
          }}
        />
      </div>
      {/* <div style={{ marginBottom: '15px' }}>
        <span style={{
          color: 'rgb(94 93 93 / 55%)',
          fontSize: '12px',
          fontFamily: 'Nunito Sans'
        }}>
          Comprehensive health monitoring and issue detection for your Kubernetes cluster resources.
        </span>
      </div> */}
      <div style={{ padding: '10px', paddingTop: "0px" }}>
        {loading ? (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '10px', fontFamily: 'Nunito Sans' }}>Loading health issues...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <img width='150px' src={noDataImg} alt="Error loading data" />
            <p style={{
              fontFamily: 'Poppins',
              fontSize: '18px',
              fontWeight: 500,
              color: '#a8b0b9',
              marginTop: '10px'
            }}>
              {error}
            </p>
          </div>
        ) : healthIssues.length > 0 ? (
          <>
          {renderIssueSummary()}
          
          {/* Tab Navigation for Active vs Resolved Issues */}
          <div style={{ marginBottom: '20px' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'active',
                  label: (
                    <span style={{ fontFamily: 'Nunito Sans', fontWeight: 600 }}>
                      {/* 🔴 Active Issues ({activeIssues.length}) */}
                      Active Issues ({activeIssues.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {activeLoading ? (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                          <Spin size="large" />
                          <p style={{ marginTop: '10px', fontFamily: 'Nunito Sans' }}>Loading active issues...</p>
                        </div>
                      ) : activeIssues.length > 0 ? (
                        <div>
                          {/* Filters for Active Issues */}
                          <div style={{ 
                            marginBottom: '20px', 
                            padding: '16px', 
                            // backgroundColor: '#fafafa', 
                            // backgroundColor: '#ffe5e5',
                            backgroundColor: '#ffe7e7',
                            borderRadius: '8px',
                            // border: '1px solid #f0f0f0'
                            // border: '1px solid #d20a0a'
                            border: '1px solid #f19595'
                          }}>
                            <div style={{ 
                              marginBottom: '16px', 
                              paddingBottom: '12px', 
                              // borderBottom: '1px solid #f0f0f0' 
                              // borderBottom: '1px solid #d20a0a'
                              borderBottom: '1px solid #f19595'
                            }}>
                              <h4 style={{ 
                                margin: 0, 
                                fontFamily: 'Nunito Sans', 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#d20a0a' 
                              }}>
                                {/* 🔍 Filter Active Issues */}
                                Filter Active Issues
                              </h4>
                            </div>
                            <Row gutter={[16, 16]} align="middle">
                              <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: '8px' }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600', 
                                    color: '#666',
                                    fontFamily: 'Nunito Sans'
                                  }}>
                                    Severity
                                  </span>
                                </div>
                                <Select
                                  value={selectedSeverity}
                                  onChange={setSelectedSeverity}
                                  style={{ width: '100%', height: '32px' }}
                                  size="small"
                                  dropdownStyle={{
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <Select.Option value="all">All Severities</Select.Option>
                                  {getUniqueValues('severity').map(severity => (
                                    <Select.Option key={severity} value={severity}>
                                      {severity.charAt(0).toUpperCase() + severity.slice(1)}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              

                              
                              <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: '8px' }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600', 
                                    color: '#666',
                                    fontFamily: 'Nunito Sans'
                                  }}>
                                    Category
                                  </span>
                                </div>
                                <Select
                                  value={selectedCategory}
                                  onChange={setSelectedCategory}
                                  style={{ width: '100%', height: '32px' }}
                                  size="small"
                                  dropdownStyle={{
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <Select.Option value="all">All Categories</Select.Option>
                                  {getUniqueValues('category').map(category => (
                                    <Select.Option key={category} value={category}>
                                      {category.charAt(0).toUpperCase() + category.slice(1)}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              
                              <Col xs={24} sm={12} md={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <div style={{ marginTop: '24px' }}>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setSelectedSeverity('all');

                                      setSelectedCategory('all');
                                    }}
                                    style={{
                                      fontFamily: 'Nunito Sans',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      color: '#666',
                                      borderColor: '#d9d9d9',
                                      backgroundColor: '#fff',
                                      height: '32px',
                                      padding: '4px 16px',
                                      borderRadius: '6px',
                                      boxShadow: '0 2px 0 rgba(0, 0, 0, 0.015)',
                                      transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.borderColor = '#40a9ff';
                                      e.target.style.color = '#40a9ff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.borderColor = '#d9d9d9';
                                      e.target.style.color = '#666';
                                    }}
                                  >
                                    Clear All Filters
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </div>
                          
                          {/* Show filtered results message if filters are applied */}
                          {filteredIssues.length === 0 && activeIssues.length > 0 && (
                            <div style={{ 
                              textAlign: 'center', 
                              marginTop: '20px',
                              marginBottom: '20px',
                              padding: '20px',
                              backgroundColor: '#fff7e6',
                              borderRadius: '8px',
                              border: '1px solid #ffd591'
                            }}>
                              <p style={{
                                fontFamily: 'Nunito Sans',
                                fontSize: '14px',
                                color: '#d46b08',
                                margin: 0
                              }}>
                                🔍 No active issues match the selected filters. Try adjusting your filter criteria.
                              </p>
                            </div>
                          )}
                          <div style={{ height: 'calc(100vh - 450px)', overflowY: 'auto' }}>
                            <Table
                              dataSource={activeTab === 'active' ? filteredIssues : activeIssues}
                              columns={columns}
                              pagination={{
                                pageSize: 20,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${activeTab === 'active' ? filteredIssues.length : activeIssues.length} active issues`
                              }}
                              size="small"
                              scroll={{ x: 1100 }}
                              rowKey="id"
                              style={{
                                fontFamily: 'Nunito Sans'
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                          <img width='120px' src={noDataImg} alt="No active issues" />
                          <p style={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#52c41a',
                            marginTop: '10px'
                          }}>
                            🎉 No Active Issues!
                          </p>
                          <p style={{
                            fontFamily: 'Nunito Sans',
                            fontSize: '14px',
                            color: '#666',
                            marginTop: '5px'
                          }}>
                            All detected issues have been resolved. Great job!
                          </p>
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  key: 'resolved',
                  label: (
                    <span style={{ fontFamily: 'Nunito Sans', fontWeight: 600 }}>
                      {/* ✅ Resolved Issues ({resolvedIssues.length}) */}
                      Resolved Issues ({resolvedIssues.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {resolvedLoading ? (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                          <Spin size="large" />
                          <p style={{ marginTop: '10px', fontFamily: 'Nunito Sans' }}>Loading resolved issues...</p>
                        </div>
                      ) : resolvedIssues.length > 0 ? (
                        <div>
                          {/* Filters for Resolved Issues */}
                          <div style={{ 
                            marginBottom: '20px', 
                            padding: '16px', 
                            backgroundColor: '#f6ffed', 
                            borderRadius: '8px',
                            border: '1px solid #b7eb8f'
                          }}>
                            <div style={{ 
                              marginBottom: '16px', 
                              paddingBottom: '12px', 
                              borderBottom: '1px solid #b7eb8f' 
                            }}>
                              <h4 style={{ 
                                margin: 0, 
                                fontFamily: 'Nunito Sans', 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#52c41a' 
                              }}>
                                {/* 🔍 Filter Resolved Issues */}
                                Filter Resolved Issues
                              </h4>
                            </div>
                            <Row gutter={[16, 16]} align="middle">
                              <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: '8px' }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600', 
                                    color: '#666',
                                    fontFamily: 'Nunito Sans'
                                  }}>
                                    Severity
                                  </span>
                                </div>
                                <Select
                                  value={selectedSeverity}
                                  onChange={setSelectedSeverity}
                                  style={{ width: '100%', height: '32px' }}
                                  size="small"
                                  dropdownStyle={{
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <Select.Option value="all">All Severities</Select.Option>
                                  {getUniqueValues('severity').map(severity => (
                                    <Select.Option key={severity} value={severity}>
                                      {severity.charAt(0).toUpperCase() + severity.slice(1)}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              
                              <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: '8px' }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600', 
                                    color: '#666',
                                    fontFamily: 'Nunito Sans'
                                  }}>
                                    Category
                                  </span>
                                </div>
                                <Select
                                  value={selectedCategory}
                                  onChange={setSelectedCategory}
                                  style={{ width: '100%', height: '32px' }}
                                  size="small"
                                  dropdownStyle={{
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <Select.Option value="all">All Categories</Select.Option>
                                  {getUniqueValues('category').map(category => (
                                    <Select.Option key={category} value={category}>
                                      {category.charAt(0).toUpperCase() + category.slice(1)}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              
                              <Col xs={24} sm={12} md={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <div style={{ marginTop: '24px' }}>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setSelectedSeverity('all');

                                      setSelectedCategory('all');
                                    }}
                                    style={{
                                      fontFamily: 'Nunito Sans',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      color: '#666',
                                      borderColor: '#d9d9d9',
                                      backgroundColor: '#fff',
                                      height: '32px',
                                      padding: '4px 16px',
                                      borderRadius: '6px',
                                      boxShadow: '0 2px 0 rgba(0, 0, 0, 0.015)',
                                      transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.borderColor = '#40a9ff';
                                      e.target.style.color = '#40a9ff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.borderColor = '#d9d9d9';
                                      e.target.style.color = '#666';
                                    }}
                                  >
                                    Clear All Filters
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </div>
                          
                          {/* Show filtered results message if filters are applied */}
                          {filteredIssues.length === 0 && resolvedIssues.length > 0 && (
                            <div style={{ 
                              textAlign: 'center', 
                              marginTop: '20px',
                              marginBottom: '20px',
                              padding: '20px',
                              backgroundColor: '#f6ffed',
                              borderRadius: '8px',
                              border: '1px solid #b7eb8f'
                            }}>
                              <p style={{
                                fontFamily: 'Nunito Sans',
                                fontSize: '14px',
                                color: '#389e0d',
                                margin: 0,
                                marginBottom: 0,
                              }}>
                                🔍 No resolved issues match the selected filters. Try adjusting your filter criteria.
                              </p>
                            </div>
                          )}
                          <div style={{ height: 'calc(100vh - 450px)', overflowY: 'auto' }}>
                            <Table
                              dataSource={activeTab === 'resolved' ? filteredIssues : resolvedIssues}
                              columns={columns}
                              pagination={{
                                pageSize: 20,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${activeTab === 'resolved' ? filteredIssues.length : resolvedIssues.length} resolved issues`
                              }}
                              size="small"
                              scroll={{ x: 1100 }}
                              rowKey="id"
                              style={{
                                fontFamily: 'Nunito Sans'
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                          <img width='120px' src={noDataImg} alt="No resolved issues" />
                          <p style={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#666',
                            marginTop: '10px'
                          }}>
                            📝 No Resolved Issues Yet
                          </p>
                          <p style={{
                            fontFamily: 'Nunito Sans',
                            fontSize: '14px',
                            color: '#666',
                            marginTop: '5px'
                          }}>
                            Issues will appear here once they are resolved.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                }
              ]}
              style={{
                fontFamily: 'Nunito Sans'
              }}
            />
          </div>
          
          {/* Legacy table for backward compatibility - can be removed later */}
          {/* {filteredIssues.length === 0 && healthIssues.length > 0 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px', 
              padding: '20px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }}>
              <p style={{
                fontFamily: 'Nunito Sans',
                fontSize: '14px',
                color: '#666',
                margin: 0
              }}>
                No issues match the selected filters. Try adjusting your filter criteria.
              </p>
            </div>
          )} */}
        </>
          // <div style={{ height: 'calc(100vh - 155px)', overflowY: 'auto' }}>
          //   <Table
          //     dataSource={healthIssues}
          //     columns={columns}
          //     pagination={{
          //       pageSize: 20,
          //       // showSizeChanger: true,
          //       showQuickJumper: true,
          //       showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} issues`
          //     }}
          //     size="small"
          //     scroll={{ x: 1100 }}
          //     rowKey="id"
          //     style={{
          //       fontFamily: 'Nunito Sans'
          //     }}
          //   />
          // </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <img width='150px' src={noDataImg} alt="No health issues found" />
            <p style={{
              fontFamily: 'Poppins',
              fontSize: '18px',
              fontWeight: 500,
              color: '#a8b0b9',
              marginTop: '10px'
            }}>
              No Health Issues Found
            </p>
            <p style={{
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              color: '#666',
              marginTop: '5px'
            }}>
              Your cluster is running smoothly with no detected issues.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthIssuesRightDrawer;